import "./index.css";

import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  AUTH_CLEARED_EVENT,
  SESSION_IDLE_TIMEOUT_MS,
  type CurrentUser,
  api,
} from "@/lib/api";
import {
  VPN_CLIENT_SYNC_INTERVAL_MS,
  canSyncVpnClients,
  syncVpnClients,
} from "./lib/vpn-client-sync";
import { LoginPage } from "@/pages/LoginPage";
import { SetupPage } from "@/pages/SetupPage";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export type View =
  | "dashboard"
  | "vpn-clients"
  | "vpn-client-detail"
  | "exports"
  | "admin"
  | "account";

export type AdminTab =
  | "destinations"
  | "certificates"
  | "app-users"
  | "mail-settings";

function triggerVpnClientsSync(user: CurrentUser | null, machine: string) {
  if (!machine || !canSyncVpnClients(user)) return;
  void syncVpnClients(machine).catch(() => {});
}

function resetActiveViewToDashboard() {
  localStorage.setItem("vpn_app_active_view", "dashboard");
}

export function App() {
  const [loading, setLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [machines, setMachines] = useState<string[]>([]);
  const [sshCertificateReady, setSshCertificateReady] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState<string>("");
  const [activeView, setActiveView] = useState<View>("dashboard");
  const [activeAdminTab, setActiveAdminTab] = useState<AdminTab>("destinations");
  const [detailClient, setDetailClient] = useState<string | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem("vpn_app_selected_machine");

    api
      .getStatus()
      .then((status) => {
        setSetupRequired(status.setupRequired);
        setCurrentUser(status.currentUser);
        setMachines(status.machines);
        setSshCertificateReady(status.sshCertificateReady);

        const machine =
          saved && status.machines.includes(saved)
            ? saved
            : (status.machines[0] ?? "");
        setSelectedMachine(machine);
        setActiveView("dashboard");
        resetActiveViewToDashboard();
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handleAuthCleared = () => setCurrentUser(null);

    window.addEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
    return () => window.removeEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    let lastActivityAt = Date.now();
    let lastKeepAliveAt = Date.now();
    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    let keepAliveInFlight = false;

    const expireForInactivity = () => {
      void api.logout().catch(() => {});
      setCurrentUser(null);
      toast.info("Sessao expirada por inatividade.");
    };

    const scheduleIdleExpiration = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(expireForInactivity, SESSION_IDLE_TIMEOUT_MS);
    };

    const keepSessionAlive = async () => {
      if (
        keepAliveInFlight ||
        Date.now() - lastActivityAt >= SESSION_IDLE_TIMEOUT_MS
      ) {
        return;
      }

      keepAliveInFlight = true;
      try {
        const status = await api.getStatus();
        if (!status.currentUser) {
          setCurrentUser(null);
          return;
        }
        setCurrentUser(status.currentUser);
        setSetupRequired(status.setupRequired);
        setMachines(status.machines);
        setSshCertificateReady(status.sshCertificateReady);
      } catch {
        /* 401 already emits AUTH_CLEARED_EVENT */
      } finally {
        keepAliveInFlight = false;
      }
    };

    const handleUserActivity = () => {
      lastActivityAt = Date.now();
      scheduleIdleExpiration();

      if (lastActivityAt - lastKeepAliveAt >= 60_000) {
        lastKeepAliveAt = lastActivityAt;
        void keepSessionAlive();
      }
    };

    const events = [
      "click",
      "focus",
      "keydown",
      "mousemove",
      "pointerdown",
      "scroll",
      "touchstart",
    ] as const;

    scheduleIdleExpiration();
    for (const event of events) {
      window.addEventListener(event, handleUserActivity, { passive: true });
    }

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      for (const event of events) {
        window.removeEventListener(event, handleUserActivity);
      }
    };
  }, [currentUser?.username]);

  useEffect(() => {
    if (!selectedMachine || !canSyncVpnClients(currentUser)) return;
    const interval = window.setInterval(() => {
      triggerVpnClientsSync(currentUser, selectedMachine);
    }, VPN_CLIENT_SYNC_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [
    currentUser?.username,
    currentUser?.permissions.join(","),
    selectedMachine,
  ]);

  const handleLogin = (
    user: CurrentUser,
    machineList: string[],
    nextCertificateReady: boolean,
  ) => {
    setCurrentUser(user);
    setMachines(machineList);
    setSshCertificateReady(nextCertificateReady);
    const saved = localStorage.getItem("vpn_app_selected_machine");
    const machine =
      saved && machineList.includes(saved) ? saved : (machineList[0] ?? "");
    setSelectedMachine(machine);
    setActiveView("dashboard");
    resetActiveViewToDashboard();
    triggerVpnClientsSync(user, machine);
  };

  const handleLogout = async () => {
    await api.logout().catch(() => {});
    setCurrentUser(null);
  };

  const handleMachineChange = (machine: string) => {
    setSelectedMachine(machine);
    localStorage.setItem("vpn_app_selected_machine", machine);
    setDetailClient(null);
    setActiveView("dashboard");
    resetActiveViewToDashboard();
    triggerVpnClientsSync(currentUser, machine);
  };

  const handleViewChange = (view: View, adminTab?: AdminTab) => {
    setActiveView(view);
    localStorage.setItem("vpn_app_active_view", view);
    if (view !== "vpn-client-detail") setDetailClient(null);
    if (view === "admin") {
      setActiveAdminTab(adminTab ?? "destinations");
    }
  };

  const handleOpenClientDetail = (clientName: string) => {
    setDetailClient(clientName);
    setActiveView("vpn-client-detail");
    localStorage.setItem("vpn_app_active_view", "vpn-client-detail");
  };

  if (loading) {
    return (
      <div className="flex min-h-svh w-full min-w-0 items-center justify-center bg-background px-6">
        <div className="flex flex-col items-center gap-3 animate-in fade-in-0 zoom-in-95 duration-500 motion-reduce:animate-none">
          <div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent motion-reduce:animate-none" />
          <span className="font-mono text-xs text-muted-foreground">
            Iniciando...
          </span>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      {setupRequired ? (
        <div className="flex min-h-svh w-full min-w-0 animate-in fade-in-0 duration-500 motion-reduce:animate-none">
          <SetupPage />
        </div>
      ) : !currentUser ? (
        <LoginPage onLogin={handleLogin} />
      ) : (
        <div className="flex min-h-svh w-full min-w-0 animate-in fade-in-0 slide-in-from-bottom-2 duration-300 motion-reduce:animate-none">
        <WorkspaceLayout
          currentUser={currentUser}
          machines={machines}
          sshCertificateReady={sshCertificateReady}
          selectedMachine={selectedMachine}
          activeView={activeView}
          activeAdminTab={activeAdminTab}
          detailClient={detailClient}
          onMachineChange={handleMachineChange}
          onViewChange={handleViewChange}
          onAdminTabChange={setActiveAdminTab}
          onOpenClientDetail={handleOpenClientDetail}
          onLogout={handleLogout}
          onUsersRefresh={(users) => {
            const me = users.find((u) => u.username === currentUser.username);
            if (me) {
              setCurrentUser({
                ...currentUser,
                permissions: me.permissions,
              });
            }
          }}
        />
        </div>
      )}
      <Toaster position="bottom-right" richColors />
    </TooltipProvider>
  );
}
